import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  SimpleGrid,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const ModernHomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');

  const heroImages = [
    '/assets/images/singha/floor1/IMG_7829.webp',
    '/assets/images/singha/floor1/IMG_7830.webp',
    '/assets/images/singha/floor1/IMG_7833.webp',
  ];

  const exhibitionImages = [
    '/assets/images/singha/floor1/IMG_7820.webp',
    '/assets/images/singha/floor1/IMG_7825.webp',
    '/assets/images/singha/floor1/IMG_7828.webp',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box position="relative" h="100vh" overflow="hidden">
        <MotionWrapper
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <Image
            src={heroImages[currentSlide]}
            alt="Hero Image"
            w="100%"
            h="100%"
            objectFit="cover"
            opacity={0.7}
          />
        </MotionWrapper>

        {/* Hero Content */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
          zIndex={2}
          w="90%"
          maxW="800px"
        >
          <MotionWrapper
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Heading
              as="h1"
              size="2xl"
              fontFamily="heading"
              color="white"
              mb={6}
              textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            >
              พิพิธภัณฑ์สิงห์ท่า
            </Heading>
            <Text
              fontSize="xl"
              color="white"
              mb={8}
              textShadow="1px 1px 2px rgba(0,0,0,0.7)"
              maxW="600px"
              mx="auto"
            >
              สำรวจประวัติศาสตร์และวัฒนธรรมของชุมชนบ้านสิงห์ท่า 
              ผ่านนิทรรศการที่บอกเล่าเรื่องราวอันทรงคุณค่า
            </Text>
            <HStack spacing={4} justify="center">
              <Button
                size="lg"
                bg="brand.500"
                _hover={{ bg: 'brand.600' }}
                onClick={() => window.location.href = '/exhibition-floor-1'}
              >
                เริ่มต้นการเดินทาง
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: 'white', color: 'brand.500' }}
                onClick={() => window.location.href = '/about-us'}
              >
                เรียนรู้เพิ่มเติม
              </Button>
            </HStack>
          </MotionWrapper>
        </Box>

        {/* Navigation Arrows */}
        <Button
          position="absolute"
          left={4}
          top="50%"
          transform="translateY(-50%)"
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.1)' }}
          onClick={prevSlide}
          zIndex={3}
        >
          ←
        </Button>
        <Button
          position="absolute"
          right={4}
          top="50%"
          transform="translateY(-50%)"
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.1)' }}
          onClick={nextSlide}
          zIndex={3}
        >
          →
        </Button>

        {/* Slide Indicators */}
        <HStack
          position="absolute"
          bottom={8}
          left="50%"
          transform="translateX(-50%)"
          spacing={2}
          zIndex={3}
        >
          {heroImages.map((_, index) => (
            <Box
              key={index}
              w={3}
              h={3}
              borderRadius="full"
              bg={index === currentSlide ? 'white' : 'rgba(255,255,255,0.5)'}
              cursor="pointer"
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </HStack>

        {/* Overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(0,0,0,0.3)"
          zIndex={1}
        />
      </Box>

      {/* Welcome Section */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
           
            transition={{ duration: 0.8 }}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <VStack align="flex-start" spacing={6}>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  ยินดีต้อนรับเข้าสู่ พิพิธภัณฑ์สิงห์ท่า
                </Heading>
                <Text fontSize="lg" color={textColor} lineHeight="1.8">
                  พิพิธภัณฑ์สิงห์ท่าก่อตั้งขึ้นเพื่อรวบรวมและเผยแพร่เรื่องราวของชุมชนสิงห์ท่า 
                  จัดแสดงนิทรรศการทางประวัติศาสตร์และวิถีชีวิตพื้นถิ่น 
                  พร้อมกิจกรรมให้ความรู้แก่ผู้เยี่ยมชมตลอดปี
                </Text>
              </VStack>
              <Box>
                <Image
                  src="/assets/images/singha/floor1/IMG_7829.webp"
                  alt="สิงห์ท่า"
                  borderRadius="lg"
                  boxShadow="lg"
                />
              </Box>
            </SimpleGrid>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Exhibition Highlights */}
      <Box py={20} bg="neutral.50">
        <Container maxW="1200px">
          <MotionWrapper
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
           
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color="neutral.900"
                >
                  ไฮไลท์นิทรรศการ
                </Heading>
                <Text fontSize="lg" color="neutral.700" maxW="600px">
                  สำรวจนิทรรศการที่น่าสนใจของเรา ที่นำเสนอประวัติศาสตร์ 
                  และวัฒนธรรมของชุมชนบ้านสิงห์ท่าผ่านการจัดแสดงที่ทันสมัย
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                {exhibitionImages.map((image, index) => (
                  <MotionWrapper
                    key={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                   
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <Box
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      transition="transform 0.3s ease"
                      _hover={{ transform: 'translateY(-8px)' }}
                    >
                      <Image
                        src={image}
                        alt={`Exhibition ${index + 1}`}
                        w="100%"
                        h="250px"
                        objectFit="cover"
                      />
                      <Box p={6}>
                        <Heading as="h3" size="md" mb={2} fontFamily="heading">
                          นิทรรศการ {index + 1}
                        </Heading>
                        <Text color="neutral.700" fontSize="sm">
                          สำรวจประวัติศาสตร์และวัฒนธรรมของชุมชนบ้านสิงห์ท่า
                        </Text>
                      </Box>
                    </Box>
                  </MotionWrapper>
                ))}
              </SimpleGrid>

              <Button
                size="lg"
                bg="brand.500"
                _hover={{ bg: 'brand.600' }}
                onClick={() => window.location.href = '/exhibition-floor-1'}
              >
                ดูนิทรรศการทั้งหมด
              </Button>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Exhibition Floor 1 Section */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
           
            transition={{ duration: 0.8 }}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <Box>
                <Image
                  src="/assets/images/singha/floor1/IMG_7829.webp"
                  alt="นิทรรศการชั้น 1"
                  borderRadius="lg"
                  boxShadow="lg"
                />
              </Box>
              <VStack align="flex-start" spacing={6}>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  นิทรรศการชั้น 1
                </Heading>
                <VStack align="flex-start" spacing={4}>
                  <Box>
                    <Heading as="h3" size="md" mb={2} fontFamily="heading" color="brand.500">
                      ส่วนจัดแสดงที่ 1 : เรื่องเล่าเมืองสิงห์ท่า
                    </Heading>
                    <Text fontSize="md" color={textColor} lineHeight="1.6">
                      ร่วมค้นหาความหมายของคำว่า สิงห์ท่า 
                      และตำนานสิงห์คู่ ที่เป็นต้นกำเนิดของชุมชนในยุคก่อบ้านตั้งเมืองจนถึงการยกบ้านสิงห์ท่าขึ้นเป็นเมืองยศสุนทร 
                      ในปี พ.ศ. 2357 และกลายเป็นจังหวัดยโสธร จังหวัดที่ 71 ของประเทศไทย
                    </Text>
                  </Box>
                  <Box>
                    <Heading as="h3" size="md" mb={2} fontFamily="heading" color="brand.500">
                      ส่วนจัดแสดงที่ 2 : สิงห์ท่าในความคิดถึง
                    </Heading>
                    <Text fontSize="md" color={textColor} lineHeight="1.6">
                      จำลองบรรยากาศโรงภาพยนตร์แห่งแรกของจังหวัดยโสธร "ยโสธรภาพยนตร์" 
                      ที่ตั้งอยู่ภายในพื้นที่ชุมชน พร้อมฉายภาพยนตร์สารคดีสั้น "สิงห์ท่าในความคิดถึง" 
                      เรื่องราวมนต์เสน่ห์ของเมืองเก่าเล่าผ่านผู้อาวุโสในชุมชน โอบล้อมไปด้วยความคิดถึงจากวันวาน
                    </Text>
                  </Box>
                </VStack>
              </VStack>
            </SimpleGrid>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={20} bg="brand.500">
        <Container maxW="1200px">
          <MotionWrapper
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
           
            transition={{ duration: 0.8 }}
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              align="center"
              justify="space-between"
              spacing={8}
            >
              <VStack align={{ base: 'center', lg: 'flex-start' }} spacing={4}>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color="white"
                  textAlign={{ base: 'center', lg: 'left' }}
                >
                  เยี่ยมชมพิพิธภัณฑ์สิงห์ท่า
                </Heading>
                <Text
                  fontSize="lg"
                  color="white"
                  textAlign={{ base: 'center', lg: 'left' }}
                  maxW="500px"
                  opacity={0.9}
                >
                  พบกับประสบการณ์การเรียนรู้ที่ไม่มีใครเหมือน 
                  ผ่านนิทรรศการที่บอกเล่าเรื่องราวของชุมชนและวัฒนธรรมท้องถิ่น
                </Text>
              </VStack>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="brand.500"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => window.location.href = '/contact'}
                >
                  จองเข้าชม
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  onClick={() => window.location.href = '/about-us'}
                >
                  เรียนรู้เพิ่มเติม
                </Button>
              </HStack>
            </Flex>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default ModernHomePage; 